/* ============================================================
   Laley PDP Clean v4 — reviews
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: reviews');
    return;
  }
  const { escapeHTML } = PDP.utils;

  const readNumber = (values) => {
    for (const value of values) {
      if (value === undefined || value === null || value === '') continue;
      const number = Number(String(value).replace(',', '.'));
      if (Number.isFinite(number)) return number;
    }
    return null;
  };

  const parseReviewStateObject = (parsed) => {
    if (!parsed || typeof parsed !== 'object') return null;
    const aggregate = parsed.aggregateRating || parsed;
    const ratingSource = parsed.reviewRating || aggregate;
    const count = readNumber([
      aggregate.reviewCount,
      aggregate.rating_count,
      aggregate.ratingCount,
      parsed.reviewCount,
      parsed.rating_count,
      parsed.ratingCount,
    ]) || 0;
    const rating = readNumber([
      aggregate.ratingValue,
      aggregate.rating?.value,
      ratingSource.ratingValue,
      ratingSource.value,
      ratingSource.rating?.value,
      parsed.rating?.value,
      parsed.rating,
    ]) || 5;
    if (count > 0) return { count, rating: Math.max(0, Math.min(5, rating)) };
    return null;
  };

  const extractJsonObjectAfter = (text, marker) => {
    const markerIndex = text.indexOf(marker);
    if (markerIndex < 0) return null;
    const start = text.indexOf('{', markerIndex);
    if (start < 0) return null;
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i += 1) {
      const char = text[i];
      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (char === '\\') {
          escaped = true;
        } else if (char === '"') {
          inString = false;
        }
        continue;
      }
      if (char === '"') {
        inString = true;
      } else if (char === '{') {
        depth += 1;
      } else if (char === '}') {
        depth -= 1;
        if (depth === 0) return text.slice(start, i + 1);
      }
    }
    return null;
  };

  const readStructuredDataReviewState = () => {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    const findState = (value) => {
      if (!value) return null;
      if (Array.isArray(value)) {
        for (const item of value) {
          const state = findState(item);
          if (state) return state;
        }
        return null;
      }
      if (typeof value !== 'object') return null;
      const state = parseReviewStateObject(value);
      if (state) return state;
      if (value['@graph']) return findState(value['@graph']);
      return null;
    };
    for (const script of scripts) {
      try {
        const state = findState(JSON.parse(script.textContent || '{}'));
        if (state) return state;
      } catch (e) {
        // Ignore unrelated or malformed JSON-LD from apps.
      }
    }
    return null;
  };

  const readKlaviyoMetafieldReviewState = () => {
    const scripts = document.querySelectorAll('script#viewed_product, script');
    for (const script of scripts) {
      const text = script.textContent || '';
      if (!text.includes('MetafieldReviews')) continue;
      const json = extractJsonObjectAfter(text, 'MetafieldReviews');
      if (!json) continue;
      try {
        const state = parseReviewStateObject(JSON.parse(json));
        if (state) return state;
      } catch (e) {
        // Klaviyo owns this script shape; ignore and fall back to widget text.
      }
    }
    return readStructuredDataReviewState();
  };

  const reviewStars = (rating) => {
    const rounded = Math.max(1, Math.min(5, Math.round(Number(rating || 5))));
    return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
  };


  const findReviewsRoot = (section) => section?.querySelector?.('#klaviyo-reviews-all') || document.querySelector('#klaviyo-reviews-all');
  const findReviewsSection = (section) => section?.querySelector?.('[data-pdp-reviews-section]') || document.querySelector('[data-pdp-reviews-section]');
  const findReviewsCountSlot = (section) => section?.querySelector?.('[data-pdp-reviews-tab-count]') || document.querySelector('[data-pdp-reviews-tab-count]');
  const hasRenderedReviews = (root) => Boolean(root?.querySelector?.('#klaviyo-product-reviews-wrapper, .reviews_all_container, .kl_reviews__list, .kl_reviews__summary'))
    && root.textContent.trim().length > 0;
  const isNearViewport = (el, margin = 900) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight + margin && rect.bottom > -margin;
  };
  const readReviewState = (section) => {
    const block = section?.querySelector?.('[data-pdp-reviews-summary]');
    const root = findReviewsRoot(section);
    const attrCount = Number(block?.dataset.reviewCount || root?.dataset.reviewCount || 0);
    const attrRating = Number(String(block?.dataset.reviewRating || root?.dataset.reviewRating || 5).replace(',', '.')) || 5;
    if (attrCount > 0) return { count: attrCount, rating: Math.max(0, Math.min(5, attrRating)) };
    return readKlaviyoMetafieldReviewState();
  };

  const primeReviewFallback = (section) => {
    const block = section?.querySelector?.('[data-pdp-reviews-summary]');
    if (!block) return;
    const root = findReviewsRoot(section);
    const countSlot = findReviewsCountSlot(section);
    const attrCount = Number(block.dataset.reviewCount || root?.dataset.reviewCount || 0);
    const attrRating = Number(String(block.dataset.reviewRating || root?.dataset.reviewRating || 5).replace(',', '.')) || 5;
    const state = attrCount > 0
      ? { count: attrCount, rating: Math.max(0, Math.min(5, attrRating)) }
      : readKlaviyoMetafieldReviewState();
    if (!state?.count) return;

    block.dataset.reviewCount = String(state.count);
    block.dataset.reviewRating = String(state.rating || 5);
    root?.setAttribute('data-review-count', String(state.count));
    root?.setAttribute('data-review-rating', String(state.rating || 5));
    if (countSlot) {
      countSlot.textContent = `(${state.count})`;
      countSlot.hidden = false;
    }
    block.classList.add('has-reviews');
    block.classList.remove('is-loading', 'is-empty', 'is-confirmed-empty');

    // VÅR rad eier visningen permanent (Sondre juli-26): Klaviyo-raden
    // skjules i CSS (beholdes usynlig som hydrerings-anker for kicket) —
    // dermed null dobbel-rad/CLS ved load, og alltid underline-typografien.
    const existing = block.querySelector('[data-pdp-reviews-fallback]');
    const fallback = existing || document.createElement('button');
    fallback.type = 'button';
    fallback.className = 'pdp-clean__reviews-fallback';
    fallback.setAttribute('data-pdp-reviews-fallback', '');
    const countWord = Number(state.count) === 1 ? 'anmeldelse' : 'anmeldelser';
    fallback.setAttribute('aria-label', `${state.count} ${countWord}`);
    fallback.innerHTML = `<span class="pdp-clean__reviews-fallback-stars" aria-hidden="true">${reviewStars(state.rating)}</span><span>${escapeHTML(state.count)} ${countWord}</span>`;
    if (!existing) block.prepend(fallback);
  };

  PDP.hooks.primeReviewFallback = primeReviewFallback;

  PDP.registerModule('reviews', {
    nudgeKlaviyoHydration() {
      const fire = () => {
        const variantForEvent = this.currentVariant || this.variantFromInput() || this.variants[0] || null;
        document.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variant: variantForEvent,
            productId: this.section.dataset.productId,
            sectionId: this.section.dataset.sectionId,
          },
        }));
        // Theme-spesifikk event for kompatibilitet med Laley-tema
        document.dispatchEvent(new CustomEvent('theme:variant:change', {
          bubbles: true,
          detail: { variant: variantForEvent, productId: this.section.dataset.productId },
        }));
        this.pokeKlaviyoReviews();
      };
      // Flere lette pulser: Klaviyo Reviews kan hydrate etter app-script, etter
      // product JSON eller etter variant events. Pulser er product-scoped og billige.
      fire();
      [650, 1600, 3200, 6400].forEach((delay) => {
        const t = window.setTimeout(fire, delay);
        this.timers.push(t);
      });
    },

    pokeKlaviyoReviews() {
      const productId = this.section.dataset.productId;
      if (!productId) return;
      this.ensureReviewsMount?.();
      const targets = new Set([
        ...this.section.querySelectorAll('[data-pdp-reviews-summary]'),
        ...document.querySelectorAll('#klaviyo-reviews-all'),
      ]);
      targets.forEach((el) => {
        el.setAttribute('data-id', productId);
        el.setAttribute('data-product-id', productId);
      });
      document.dispatchEvent(new CustomEvent('laley:pdp:reviews-ready', {
        bubbles: true,
        detail: { productId, sectionId: this.section.dataset.sectionId },
      }));
    },

    ensureReviewsMount() {
      const existing = findReviewsRoot(this.section);
      if (existing) return existing;

      const state = readReviewState(this.section);
      if (!state?.count) return null;

      const productId = this.section.dataset.productId;
      const sectionId = this.section.dataset.sectionId || productId || 'reviews';
      const sectionToken = String(sectionId).replace(/[^A-Za-z0-9_-]/g, '-');
      const reviewsSection = document.createElement('section');
      reviewsSection.className = 'pdp-clean__reviews-section pdp-clean__reviews-section--standalone is-loading';
      reviewsSection.setAttribute('data-pdp-reviews-section', '');
      reviewsSection.setAttribute('aria-labelledby', `pdp-reviews-title-${sectionToken}`);
      const rating = Number(state.rating || 5) || 5;
      reviewsSection.innerHTML = `
        <div class="pdp-clean__reviews-head" data-pdp-reviews-head>
          <h2 class="pdp-clean__reviews-head-title" id="pdp-reviews-title-${escapeHTML(sectionToken)}">Anmeldelser (${escapeHTML(state.count)})</h2>
          <button type="button" class="pdp-clean__reviews-write" data-pdp-reviews-write>Skriv anmeldelse</button>
        </div>
        <div id="klaviyo-reviews-all" data-id="${escapeHTML(productId)}" data-product-id="${escapeHTML(productId)}" data-review-count="${escapeHTML(state.count)}" data-review-rating="${escapeHTML(rating)}"></div>
      `;

      const host = this.section.closest('.shopify-section') || this.section;
      host.insertAdjacentElement('afterend', reviewsSection);
      return reviewsSection.querySelector('#klaviyo-reviews-all');
    },

    muteDocScroll(ms) {
      if (this.docScrollMuted) return;
      this.docScrollMuted = true;
      const saved = [];
      const isDoc = (el) => el === document.scrollingElement || el === document.documentElement || el === document.body;
      const mute = (obj, key, guard) => {
        const orig = obj[key];
        if (typeof orig !== 'function') return;
        saved.push([obj, key, orig]);
        obj[key] = guard
          ? function (...args) { if (guard(this)) return undefined; return orig.apply(this, args); }
          : function () { return undefined; };
      };
      mute(window, 'scrollTo');
      mute(window, 'scrollBy');
      mute(Element.prototype, 'scrollIntoView');
      mute(Element.prototype, 'scrollTo', isDoc);
      mute(Element.prototype, 'scrollBy', isDoc);
      this.restoreDocScroll = () => {
        if (!this.docScrollMuted) return;
        saved.reverse().forEach(([obj, key, orig]) => { obj[key] = orig; });
        this.docScrollMuted = false;
      };
      const timer = window.setTimeout(this.restoreDocScroll, ms);
      this.timers.push(timer);
    },

    // Brukerintensjon slår ALLTID demping: jump/skriv-anmeldelse-klikk
    // kolliderte med auto-kickenes mutevindu (ticker ~2s + 1.2s mute =
    // døv 60 % av tiden mens widgeten er urendret).
    unmuteDocScroll() {
      if (this.restoreDocScroll) this.restoreDocScroll();
    },

    kickKlaviyoReviewsHydration() {
      let root = this.ensureReviewsMount?.() || findReviewsRoot(this.section);
      if (root && hasRenderedReviews(root)) return false;

      this.pokeKlaviyoReviews();
      window.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
      root = findReviewsRoot(this.section);
      if (root && hasRenderedReviews(root)) return true;

      const now = Date.now();
      if (this.reviewHydrationKickAt && now - this.reviewHydrationKickAt < 900) return false;
      this.reviewHydrationKickAt = now;

      const widget = this.section.querySelector('[data-pdp-reviews-summary] .kl_reviews__star_rating_widget, [data-pdp-reviews-summary] .klaviyo-star-rating-widget, [data-pdp-reviews-summary] [class*="star_rating"]');
      if (!widget) return false;

      // ÉN enkel demper (erstatter gen 1-4): Klaviyos hopp kommer som svar
      // på VÅRT syntetiske klikk — demp dokument-scroll-APIene i nøyaktig
      // det vinduet. Tidsskopet, ikke tilstandsskopet. Galleriets egne
      // el.scrollTo (ikke-dokument) passerer via isDoc-guarden.
      this.muteDocScroll(1200);

      const event = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
      Object.defineProperty(event, '__laleySilentReviewHydrate', { value: true });
      widget.dispatchEvent(event);
      [0, 80, 240, 720].forEach((delay) => {
        const timer = window.setTimeout(() => this.pokeKlaviyoReviews(), delay);
        this.timers.push(timer);
      });
      return true;
    },

    // "7 reviews" → "7 anmeldelser" — leser det FAKTISKE antallet fra Klaviyo-widgeten
    // og bytter kun ordet (dynamisk, ikke hardkodet tall). Best-effort: virker der
    // teksten er i light-DOM (ikke shadow-DOM).
    bindReviewLabelLocale() {
      const block = this.section.querySelector('[data-pdp-jump-reviews]');
      const countSlot = findReviewsCountSlot(this.section);
      if (!block) return;
      const cacheKey = `laley:pdp:review-summary:${this.section.dataset.productId || 'unknown'}`;
      const readCache = () => {
        try {
          const value = window.localStorage?.getItem(cacheKey);
          if (!value) return null;
          const parsed = JSON.parse(value);
          const fresh = Date.now() - Number(parsed.ts || 0) < 1000 * 60 * 60 * 24 * 14;
          return fresh && Number(parsed.count) > 0 ? parsed : null;
        } catch (e) {
          return null;
        }
      };
      const writeCache = (state) => {
        try {
          window.localStorage?.setItem(cacheKey, JSON.stringify({ ...state, ts: Date.now() }));
        } catch (e) {
          // localStorage can be disabled in private mode; reviews still hydrate normally.
        }
      };
      const readEmbeddedReviewState = () => readKlaviyoMetafieldReviewState();
      const extractState = () => {
        const root = findReviewsRoot(this.section);
        const attrCount = Number(block.dataset.reviewCount || root?.dataset.reviewCount || 0);
        const attrRating = Number(String(block.dataset.reviewRating || root?.dataset.reviewRating || 5).replace(',', '.')) || 5;
        if (attrCount > 0) {
          return { count: attrCount, rating: Math.max(0, Math.min(5, attrRating)) };
        }

        const embedded = readEmbeddedReviewState();
        if (embedded?.count > 0) return embedded;

        const candidates = [block.textContent || ''];
        block.querySelectorAll('[aria-label], [title], [data-testid], [class]').forEach((el) => {
          candidates.push(el.getAttribute('aria-label') || '');
          candidates.push(el.getAttribute('title') || '');
          candidates.push(el.getAttribute('data-testid') || '');
          candidates.push(el.className || '');
        });
        const joined = candidates.join(' ');
        const countHit = candidates
          .map((text) => String(text).match(/(\d+)\s*(?:reviews?|anmeldelser?|anmeldelse)\b/i))
          .find(Boolean);
        const ratingHit = joined.match(/([0-5](?:[.,]\d+)?)\s*(?:out of|av)\s*5/i)
          || joined.match(/rating[:\s]+([0-5](?:[.,]\d+)?)/i);
        const count = Number(countHit?.[1] || 0);
        const rating = Number(String(ratingHit?.[1] || 5).replace(',', '.')) || 5;
        return { count, rating: Math.max(0, Math.min(5, rating)) };
      };
      const renderFallback = (state) => {
        if (!state || Number(state.count) < 1) return;
        // Vår rad eier visningen permanent — Klaviyo-raden er skjult i CSS
        // og skal aldri erstatte fallbacken (dobbel rad/CLS ved load).
        const existing = block.querySelector('[data-pdp-reviews-fallback]');
        const rating = Math.round(Number(state.rating || 5) * 2) / 2;
        const fullStars = Math.max(0, Math.min(5, Math.floor(rating)));
        const halfStar = rating % 1 >= 0.5;
        const stars = `${'★'.repeat(fullStars)}${halfStar ? '½' : ''}${'☆'.repeat(Math.max(0, 5 - fullStars - (halfStar ? 1 : 0)))}`;
        const fallback = existing || document.createElement('button');
        fallback.type = 'button';
        fallback.className = 'pdp-clean__reviews-fallback';
        fallback.setAttribute('data-pdp-reviews-fallback', '');
        const countWord = Number(state.count) === 1 ? 'anmeldelse' : 'anmeldelser';
        fallback.setAttribute('aria-label', `${state.count} ${countWord}`);
        fallback.innerHTML = `<span class="pdp-clean__reviews-fallback-stars" aria-hidden="true">${stars}</span><span>${state.count} ${countWord}</span>`;
        if (!existing) block.prepend(fallback);
      };
      const setCount = (value) => {
        if (!countSlot) return;
        const count = Number(String(value || '').match(/\d+/)?.[0] || 0);
        if (!Number.isFinite(count) || count < 1) return;
        countSlot.textContent = `(${count})`;
        countSlot.hidden = false;
      };
      const syncCount = () => {
        const state = extractState();
        const cached = state.count > 0 ? state : readCache();
        if (cached?.count > 0) {
          setCount(cached.count);
          renderFallback(cached);
          block.classList.add('has-reviews');
          block.classList.remove('is-loading', 'is-empty', 'is-confirmed-empty');
          if (state.count > 0) writeCache(state);
          return true;
        }
        return false;
      };
      const localize = () => {
        const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
          acceptNode(node) {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            if (/^(SCRIPT|STYLE|SVG|PATH|STOP)$/i.test(parent.tagName)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        let node;
        while ((node = walker.nextNode())) {
          const t = node.nodeValue;
          if (t && /\breviews?\b/i.test(t)) {
            const nt = t.replace(/\breviews\b/gi, 'anmeldelser').replace(/\breview\b/gi, 'anmeldelse');
            if (nt !== t) node.nodeValue = nt;
          }
        }
        syncCount();
      };
      let raf = null;
      const schedule = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = null;
          localize();
        });
      };
      localize();
      const obs = new MutationObserver(schedule);
      obs.observe(block, { childList: true, subtree: true, characterData: true });
      [900, 2200, 4600].forEach((delay) => {
        const timer = window.setTimeout(localize, delay);
        this.timers.push(timer);
      });
      const emptyTimer = window.setTimeout(() => {
        if (syncCount()) return;
        block.classList.add('is-empty', 'is-confirmed-empty');
        block.classList.remove('is-loading', 'has-reviews');
      }, 3000);
      this.timers.push(emptyTimer);
      this.unsubs.push(() => {
        if (raf) window.cancelAnimationFrame(raf);
        obs.disconnect();
      });
    },

    bindReviewWidgetPolish() {
      const root = this.ensureReviewsMount?.() || findReviewsRoot(this.section);
      if (!root) {
        if (this.reviewPolishDeferred) return;
        this.reviewPolishDeferred = true;
        let retries = 0;
        let timer = null;
        const retry = () => {
          retries += 1;
          const nextRoot = this.ensureReviewsMount?.() || findReviewsRoot(this.section);
          if (nextRoot) {
            cleanup();
            this.bindReviewWidgetPolish();
            this.kickKlaviyoReviewsHydration();
            return;
          }
          if (retries >= 12) cleanup();
        };
        const cleanup = () => {
          this.reviewPolishDeferred = false;
          if (timer) window.clearInterval(timer);
          observer?.disconnect();
        };
        const observer = new MutationObserver(retry);
        observer.observe(document.body, { childList: true, subtree: true });
        timer = window.setInterval(retry, 900);
        this.unsubs.push(cleanup);
        return;
      }
      if (root.dataset.pdpReviewsPolishBound === 'true') return;
      root.dataset.pdpReviewsPolishBound = 'true';

      const countSlot = findReviewsCountSlot(this.section);
      const section = findReviewsSection(this.section);
      let visibilityReady = false;
      let latestReviewCount = 0;
      const summary = this.section.querySelector('[data-pdp-reviews-summary]');
      const setCount = (value) => {
        const count = Number(String(value || '').match(/\d+/)?.[0] || 0);
        if (!Number.isFinite(count) || count < 1) return;
        latestReviewCount = count;
        if (countSlot) {
          countSlot.textContent = `(${count})`;
          countSlot.hidden = false;
        }
      };
      setCount(root.dataset.reviewCount || summary?.dataset.reviewCount || '');

      const translate = (value) => {
        let text = String(value || '');
        const trimmed = text.trim();
        const exact = {
          'Customer reviews': 'Kundeanmeldelser',
          'WRITE A REVIEW': 'Skriv anmeldelse',
          'Write a review': 'Skriv anmeldelse',
          Reviews: 'Anmeldelser',
          Review: 'Anmeldelse',
          'SHOW MORE': 'Vis flere',
          'Show more': 'Vis flere',
          'Verified buyer': 'Verifisert kjøper',
          'Most recent': 'Nyeste først',
          'Highest rating': 'Høyest vurdering',
          'Lowest rating': 'Lavest vurdering',
          'Most relevant': 'Mest relevant',
          'All ratings': 'Alle vurderinger',
          'With media': 'Med bilder',
          'Search reviews': 'Søk i anmeldelser',
        };
        if (exact[trimmed]) return text.replace(trimmed, exact[trimmed]);

        text = text
          .replace(/\bCustomer reviews\b/g, 'Kundeanmeldelser')
          .replace(/\bWRITE A REVIEW\b/g, 'Skriv anmeldelse')
          .replace(/\bWrite a review\b/g, 'Skriv anmeldelse')
          .replace(/\bSHOW MORE\b/g, 'Vis flere')
          .replace(/\bShow more\b/g, 'Vis flere')
          .replace(/\bVerified buyer\b/g, 'Verifisert kjøper')
          .replace(/\b(\d+)\s+reviews\b/gi, (_, count) => `${count} anmeldelser`)
          .replace(/\b1\s+review\b/gi, '1 anmeldelse')
          .replace(/\b(\d+)\s+ratings\b/gi, (_, count) => `${count} vurderinger`)
          .replace(/\b1\s+rating\b/gi, '1 vurdering')
          .replace(/\b(\d+)\s+stars\b/gi, (_, count) => `${count} stjerner`)
          .replace(/\b1\s+star\b/gi, '1 stjerne')
          .replace(/\b1\s+day ago\b/gi, 'for 1 dag siden')
          .replace(/\b(\d+)\s+days ago\b/gi, (_, count) => `for ${count} dager siden`)
          .replace(/\b1\s+month ago\b/gi, 'for 1 måned siden')
          .replace(/\b(\d+)\s+months ago\b/gi, (_, count) => `for ${count} måneder siden`)
          .replace(/\b1\s+year ago\b/gi, 'for 1 år siden')
          .replace(/\b(\d+)\s+years ago\b/gi, (_, count) => `for ${count} år siden`);
        return text;
      };

      const syncCount = () => {
        // Use Klaviyo's explicit count nodes only. Broad root text also contains
        // histogram/filter numbers (5 stars, 4 stars, etc.) and can corrupt the
        // PDP tab count.
        const candidates = [
          root.querySelector('.kl_reviews__list__tab small')?.textContent || '',
          root.querySelector('.kl_reviews__summary__stars__count')?.textContent || '',
        ];
        const hit = candidates
          .map((text) => String(text).match(/\b(\d+)\b/))
          .find(Boolean);
        if (hit) setCount(hit[1]);
      };

      const syncVisibility = () => {
        if (!section) return;
        const reviewItems = root.querySelectorAll('.kl_reviews__review_item, .kl_reviews__review__item, [class*="review_item"], [class*="review__item"]');
        const hasRenderedWidget = hasRenderedReviews(root);
        const explicitCount = [
          root.dataset.reviewCount || '',
          summary?.dataset.reviewCount || '',
          root.querySelector('.kl_reviews__list__tab small')?.textContent || '',
          root.querySelector('.kl_reviews__summary__stars__count')?.textContent || '',
        ]
          .map((text) => Number(String(text).match(/\b(\d+)\b/)?.[1] || 0))
          .find((count) => Number.isFinite(count) && count > 0) || 0;
        const hasKnownReviews = latestReviewCount > 0 || explicitCount > 0;
        // En rendret widget med NULL anmeldelser («0 / 5»-summary) er en tom
        // tilstand, ikke innhold — seksjonen skjules til første anmeldelse
        // finnes (Sondre juli-26: «no reviews yet»/0-histogram er anti-vibe).
        if (reviewItems.length > 0 || (hasRenderedWidget && hasKnownReviews)) {
          root.querySelector('[data-pdp-reviews-fallback-all]')?.remove();
          section.classList.add('has-reviews');
          section.classList.remove('is-loading', 'is-empty', 'is-confirmed-empty');
          return;
        }
        if (hasRenderedWidget && !hasKnownReviews) {
          section.classList.add('is-empty', 'is-confirmed-empty');
          section.classList.remove('is-loading', 'has-reviews');
          return;
        }
        if (hasKnownReviews) {
          section.classList.add('has-reviews');
          section.classList.remove('is-empty', 'is-confirmed-empty');
          if (root.querySelector('[data-pdp-reviews-fallback-all]')) {
            section.classList.remove('is-loading');
          } else {
            section.classList.add('is-loading');
          }
          return;
        }
        if (visibilityReady) {
          section.classList.add('is-empty', 'is-confirmed-empty');
          section.classList.remove('is-loading', 'has-reviews');
        }
      };

      const polish = () => {
        if (section && isNearViewport(section) && !hasRenderedReviews(root)) {
          this.kickKlaviyoReviewsHydration();
        }

        const walker = document.createTreeWalker(root, 4, {
          acceptNode(node) {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            if (/^(SCRIPT|STYLE|SVG|PATH|STOP)$/i.test(parent.tagName)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        let node;
        while ((node = walker.nextNode())) {
          const next = translate(node.nodeValue);
          if (next !== node.nodeValue) node.nodeValue = next;
        }

        root.querySelectorAll('[aria-label], [title], [placeholder]').forEach((el) => {
          ['aria-label', 'title', 'placeholder'].forEach((attr) => {
            const value = el.getAttribute(attr);
            if (!value) return;
            const next = translate(value);
            if (next !== value) el.setAttribute(attr, next);
          });
        });
        syncCount();
        syncVisibility();
      };

      let polishTimer = null;
      const schedule = () => {
        if (polishTimer) return;
        polishTimer = window.setTimeout(() => {
          polishTimer = null;
          polish();
        }, 90);
      };

      polish();
      const visibilityTimer = window.setTimeout(() => {
        visibilityReady = true;
        polish();
      }, 3800);
      [1200, 2600, 5200, 8800].forEach((delay) => {
        const timer = window.setTimeout(() => {
          if (section && isNearViewport(section, 1200) && !hasRenderedReviews(root)) {
            this.kickKlaviyoReviewsHydration();
          }
          polish();
        }, delay);
        this.timers.push(timer);
      });
      const obs = new MutationObserver(schedule);
      obs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-label', 'title', 'placeholder'] });
      let intersectionObserver = null;
      if ('IntersectionObserver' in window && section) {
        intersectionObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.kickKlaviyoReviewsHydration();
          polish();
        }, { rootMargin: '720px 0px' });
        intersectionObserver.observe(section);
      }
      // Utrettelig hydrering + aldri-dødt skjelett: Klaviyo kan bomme på alle
      // de tidlige kickene (trege app-scripts, throttle-vinduer). Fortsett å
      // prøve hvert 2. sekund så lenge seksjonen er nær viewport, og vis
      // metafelt-summary som fallback etter 16 s i stedet for et evig
      // «Laster anmeldelser»-skjelett (brukerrapportert på date-night).
      const retryTicker = window.setInterval(() => {
        if (hasRenderedReviews(root)) {
          window.clearInterval(retryTicker);
          return;
        }
        if (section && isNearViewport(section, 1200)) {
          this.kickKlaviyoReviewsHydration();
          polish();
        }
      }, 2000);
      const retryStop = window.setTimeout(() => window.clearInterval(retryTicker), 30000);
      const fallbackTimer = window.setTimeout(() => {
        if (hasRenderedReviews(root)) return;
        const state = readReviewState(this.section);
        const count = latestReviewCount || Number(state?.count || 0);
        if (!count || !section) return;
        if (!root.querySelector('[data-pdp-reviews-fallback-all]')) {
          const rating = Number(state?.rating || 5) || 5;
          const node = document.createElement('div');
          node.className = 'pdp-clean__reviews-fallback-all';
          node.setAttribute('data-pdp-reviews-fallback-all', '');
          const countWord = Number(count) === 1 ? 'anmeldelse' : 'anmeldelser';
          node.innerHTML = `<span class="pdp-clean__reviews-fallback-stars" aria-hidden="true">${reviewStars(rating)}</span><span>${count} ${countWord}</span>`;
          root.appendChild(node);
        }
        section.classList.remove('is-loading');
        section.classList.add('has-reviews');
      }, 16000);
      this.timers.push(retryStop, fallbackTimer);
      this.unsubs.push(() => {
        if (polishTimer) window.clearTimeout(polishTimer);
        window.clearTimeout(visibilityTimer);
        window.clearInterval(retryTicker);
        obs.disconnect();
        intersectionObserver?.disconnect();
      });
    },

    bindReviewsJump() {
      this.ensureReviewsMount?.();
      const links = this.section.querySelectorAll('[data-pdp-jump-reviews]');
      const target = findReviewsSection(this.section) || this.section.querySelector('[data-pdp-accordions]');
      if (!links.length || !target) return;
      const jump = (event) => {
        if (event?.__laleySilentReviewHydrate) {
          this.pokeKlaviyoReviews();
          return;
        }
        if (event) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation?.();
        }
        this.activateReviewsTab?.();
        this.pokeKlaviyoReviews();
        this.unmuteDocScroll();
        const scrollToReviews = (behavior = 'smooth') => {
          const headerVar = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--HEADER-HEIGHT'), 10);
          const header = Number.isFinite(headerVar) ? headerVar : (document.querySelector('header')?.offsetHeight || 0);
          const top = target.getBoundingClientRect().top + window.scrollY - header - 12;
          window.scrollTo({ top: Math.max(0, top), behavior });
          if (target.id) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${target.id}`);
        };
        window.requestAnimationFrame(() => {
          scrollToReviews('smooth');
          window.setTimeout(() => {
            const headerVar = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--HEADER-HEIGHT'), 10);
            const header = Number.isFinite(headerVar) ? headerVar : (document.querySelector('header')?.offsetHeight || 0);
            const delta = Math.abs(target.getBoundingClientRect().top - header - 12);
            if (delta > 36) scrollToReviews('auto');
            target.setAttribute('tabindex', '-1');
            target.focus?.({ preventScroll: true });
          }, 620);
        });
      };
      links.forEach((link) => {
        if (link.dataset.pdpReviewsJumpBound === 'true') return;
        link.dataset.pdpReviewsJumpBound = 'true';
        if (link.tagName !== 'A') {
          if (!link.hasAttribute('tabindex')) link.tabIndex = 0;
          if (!link.hasAttribute('role')) link.setAttribute('role', 'button');
        }
        this.on(link, 'click', jump, { capture: true });
        this.on(link, 'keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          jump(event);
        });
      });
    },

    bindAllReviewsHeader() {
      // «Skriv anmeldelse» bodde i Klaviyos tabs-rad, som skjules fordi den
      // dublerte tittelen. Vi eier headeren: tittel + ghost-knapp som
      // proxy-klikker Klaviyos skjulte knapp. Root kan byttes ut av widgeten
      // — alt slås opp ferskt, og raden bor i forelderen og overlever bytter.
      const getRoot = () => findReviewsRoot(this.section);
      const initialRoot = getRoot();
      if (!initialRoot || !initialRoot.parentElement) return;
      const host = initialRoot.parentElement;
      const readCount = () => {
        const root = getRoot();
        const tab = root?.querySelector('.kl_reviews__list__tab');
        const fromTab = String(tab?.textContent || '').match(/(\d+)/);
        if (fromTab) return Number(fromTab[1]);
        const fromData = Number(root?.dataset.reviewCount || 0);
        return Number.isFinite(fromData) ? fromData : 0;
      };
      const clickKlaviyoWrite = () => {
        const root = getRoot();
        const target = root?.querySelector('.kl_reviews__button_bar button, button.kl_reviews__button');
        if (target) {
          target.click();
          return true;
        }
        return false;
      };
      const ensure = () => {
        const root = getRoot();
        if (!root) return;
        // Klaviyo kan sette inline display på tabs-raden — inline vinner
        // over stylesheet-!important, så vi nuller den her også.
        root.querySelectorAll('.kl_reviews__list__tabs, .kl_reviews__summary__header').forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
        });
        const directReviewHeads = () => Array.from(host.children || [])
          .filter((node) => node.classList?.contains('pdp-clean__reviews-head'));
        let head = host.querySelector('[data-pdp-reviews-head]') || directReviewHeads()[0];
        if (!head) {
          head = document.createElement('div');
          head.className = 'pdp-clean__reviews-head';
          head.innerHTML = '<h2 class="pdp-clean__reviews-head-title"></h2>'
            + '<button type="button" class="pdp-clean__reviews-write" data-pdp-reviews-write>Skriv anmeldelse</button>';
          host.insertBefore(head, root);
        }
        head.setAttribute('data-pdp-reviews-head', '');
        directReviewHeads().forEach((node) => {
          if (node !== head) node.remove();
        });

        let title = head.querySelector('.pdp-clean__reviews-head-title');
        const legacyTitle = head.querySelector('.pdp-clean__reviews-title');
        if (!title && legacyTitle) {
          legacyTitle.className = 'pdp-clean__reviews-head-title';
          title = legacyTitle;
        }
        if (!title) {
          title = document.createElement('h2');
          title.className = 'pdp-clean__reviews-head-title';
          head.prepend(title);
        }
        title.querySelector('[data-pdp-reviews-tab-count]')?.remove();
        let write = head.querySelector('[data-pdp-reviews-write]');
        if (!write) {
          write = document.createElement('button');
          write.type = 'button';
          write.className = 'pdp-clean__reviews-write';
          write.setAttribute('data-pdp-reviews-write', '');
          write.textContent = 'Skriv anmeldelse';
          head.appendChild(write);
        }
        if (!write.dataset.pdpReviewsBound) {
          write.dataset.pdpReviewsBound = 'true';
          this.on(write, 'click', () => {
            this.unmuteDocScroll();
            if (clickKlaviyoWrite()) return;
            this.kickKlaviyoReviewsHydration();
            [600, 1600, 3200].forEach((delay) => {
              const timer = window.setTimeout(() => { clickKlaviyoWrite(); }, delay);
              this.timers.push(timer);
            });
          });
        }
        const count = readCount();
        const next = count > 0 ? `Anmeldelser (${count})` : 'Anmeldelser';
        if (title && title.textContent !== next) title.textContent = next;
      };
      ensure();
      let headTimer = null;
      const schedule = () => {
        if (headTimer) return;
        headTimer = window.setTimeout(() => {
          headTimer = null;
          ensure();
        }, 120);
      };
      const obs = new MutationObserver(schedule);
      obs.observe(host, { childList: true, subtree: true });
    },

    bindKundeneSier() {
      // Keep the buy column deterministic. Klaviyo can hydrate the rating
      // summary and the full reviews section, but it must not inject a delayed
      // proof card into the decision stack. If a product needs a featured quote,
      // use the product metafields rendered in Liquid instead.
      const slot = this.section.querySelector('[data-pdp-kundene-slot]');
      if (slot) {
        slot.hidden = true;
        slot.innerHTML = '';
        slot.removeAttribute('role');
        slot.removeAttribute('tabindex');
        slot.removeAttribute('aria-label');
        slot.style.removeProperty('cursor');
      }
    },
  });
})();
